import React, { useState, useEffect } from 'react';
import {
  FaGraduationCap, FaBriefcase, FaChartLine, FaGlobe, FaCheckCircle, FaClock,
  FaUsers, FaRocket, FaBook, FaCalendarAlt, FaBuilding, FaAward, FaStar,
  FaBars, FaTimes, FaChevronDown, FaChevronUp, FaFlag, FaBookOpen,
  FaChalkboardTeacher, FaRegFileAlt, FaUserGraduate, FaDownload, FaPlay,
  FaLaptop, FaCertificate, FaMoneyBillWave, FaMapMarkerAlt, FaPhoneAlt,
  FaEnvelope, FaInfoCircle
} from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import CATrainingIndia from '../ca_programs/ca_national_level';
import CharteredAccountantPlatform from '../ca_programs/ca_international_level';

const CATraining = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [mode, setMode] = useState('national');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [expandedModule, setExpandedModule] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', programId: '', course: '' });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const nationalPrograms = [
    {
      id: 'nat-foundation-6m',
      title: 'CA Foundation - 6 Month Intensive',
      tagline: 'IIT-style conceptual training + ICAI exam preparation',
      duration: '6 months',
      startDate: 'Nov 15, 2025',
      fees: '₹29,999',
      seats: 30,
      delivery: 'Hybrid (Online + Weekend Labs)',
      eligibility: '+2 Students',
      certification: 'ICAI Foundation Certificate',
      highlights: [
        'Complete CA Foundation syllabus mapped to ICAI',
        'Daily problem-solving sessions with IIT-style approach',
        'Weekly mock tests and performance tracking',
        'Tally, Excel & GST practical training',
        '2-week internship with CA firms',
        'Articleship guidance and placement support'
      ],
      weeklySchedule: {
        'Monday': ['9:00 AM - Accounting Principles', '2:00 PM - Problem Solving', '5:00 PM - Doubt Clearing'],
        'Tuesday': ['9:00 AM - Business Laws', '2:00 PM - Case Studies', '5:00 PM - Group Discussion'],
        'Wednesday': ['9:00 AM - Business Mathematics', '2:00 PM - Practice Tests', '5:00 PM - Mentor Session'],
        'Thursday': ['9:00 AM - Economics Concepts', '2:00 PM - Industry Talk', '5:00 PM - Project Work'],
        'Friday': ['9:00 AM - Commercial Knowledge', '2:00 PM - Mock Test', '5:00 PM - Performance Review'],
        'Saturday': ['9:00 AM - Practical Labs (Tally/Excel)', '2:00 PM - GST Workshop', '5:00 PM - Free Practice'],
        'Sunday': ['Rest Day / Self Study / Optional Doubt Sessions']
      },
      modules: [
        {
          name: 'Principles & Practice of Accounting',
          duration: '8 weeks',
          topics: [
            'Theoretical Framework - Accounting Standards',
            'Accounting Process - Journal to Financial Statements',
            'Bank Reconciliation Statement',
            'Inventories and Depreciation Accounting',
            'Bills of Exchange and Consignment Accounts',
            'Partnership Accounts - Admission, Retirement, Dissolution',
            'Company Accounts - Share Capital and Debentures',
            'Financial Statements Analysis'
          ],
          practicals: ['Tally ERP Training', 'Excel Financial Modeling', 'Real-world Case Studies']
        },
        {
          name: 'Business Laws & Business Correspondence',
          duration: '6 weeks',
          topics: [
            'Indian Contract Act 1872',
            'Sale of Goods Act 1930',
            'Indian Partnership Act 1932',
            'Limited Liability Partnership Act 2008',
            'Companies Act 2013 - Formation and Management',
            'Business Communication Skills',
            'Letter Writing and Report Preparation',
            'Email Etiquette and Professional Communication'
          ],
          practicals: ['Legal Case Analysis', 'Business Document Drafting', 'Presentation Skills']
        },
        {
          name: 'Business Mathematics, Logical Reasoning & Statistics',
          duration: '7 weeks',
          topics: [
            'Ratio, Proportion, Indices and Logarithms',
            'Equations and Matrices',
            'Linear Inequalities',
            'Time Value of Money - Simple & Compound Interest',
            'Permutations and Combinations',
            'Sequence and Series - AP, GP, HP',
            'Sets, Relations and Functions',
            'Basic Concepts of Probability',
            'Measures of Central Tendency and Dispersion',
            'Correlation and Regression',
            'Index Numbers and Time Series'
          ],
          practicals: ['IIT-JEE Style Problem Solving', 'Statistical Software Training', 'Data Analysis Projects']
        },
        {
          name: 'Business Economics & Commercial Knowledge',
          duration: '5 weeks',
          topics: [
            'Introduction to Microeconomics',
            'Theory of Demand and Supply',
            'Theory of Production and Cost',
            'Price Determination under Different Markets',
            'Business Cycles and Economic Systems',
            'Indian Economy Overview',
            'Banking and Insurance in India',
            'Government Policies and Economic Reforms',
            'International Trade and WTO',
            'GST - Goods and Services Tax'
          ],
          practicals: ['Economic Data Analysis', 'Current Affairs Integration', 'Industry Research Projects']
        }
      ],
      careerPath: [
        'CA Intermediate Preparation',
        'Articleship Training (3 years)',
        'CA Final Examination',
        'Chartered Accountant Practice',
        'Corporate Finance Roles'
      ],
      internshipDetails: {
        duration: '2 weeks',
        partners: ['Top CA Firms in Mumbai', 'Corporate Finance Departments', 'Tax Consulting Firms'],
        activities: ['Audit Assistance', 'Tax Return Filing', 'Financial Statement Preparation', 'GST Compliance']
      }
    },
    {
      id: 'nat-foundation-1y',
      title: 'CA Foundation - 1 Year Comprehensive',
      tagline: 'Deep conceptual training + Multiple internships + International exposure',
      duration: '12 months',
      startDate: 'Jan 10, 2026',
      fees: '₹59,999',
      seats: 25,
      delivery: 'Blended (Weekday Online + Weekend Labs)',
      eligibility: '+2 / +1 Students',
      certification: 'ICAI Foundation + ACCA Primer Certificate',
      highlights: [
        'Complete CA Foundation + Advanced modules',
        'Two internships (4 weeks + 6 weeks)',
        'ACCA/CPA primer modules as add-ons',
        'Advanced Excel, Tally, SAP training',
        'Soft skills and personality development',
        'Interview preparation and resume building',
        'Placement assistance with Big 4 firms'
      ],
      weeklySchedule: {
        'Monday-Wednesday': ['Core CA Foundation subjects with advanced problem solving'],
        'Thursday': ['Industry expert sessions and guest lectures'],
        'Friday': ['Mock tests and performance evaluation'],
        'Saturday': ['Practical labs - Software training'],
        'Sunday': ['Internship activities and project work']
      },
      modules: [
        {
          name: 'Advanced Accounting & Financial Analysis',
          duration: '10 weeks',
          topics: [
            'All CA Foundation Accounting Topics (Advanced Level)',
            'Financial Statement Analysis - Ratio Analysis',
            'Cash Flow Statement Preparation',
            'Fund Flow Statement',
            'Working Capital Management',
            'Budgetary Control',
            'Standard Costing and Variance Analysis',
            'Management Accounting Techniques',
            'IFRS vs Indian AS - Basic Comparison'
          ],
          practicals: ['Advanced Tally Features', 'SAP FICO Basics', 'Financial Modeling in Excel', 'Real Company Analysis']
        },
        {
          name: 'Corporate Law & Taxation',
          duration: '10 weeks',
          topics: [
            'Companies Act 2013 - Comprehensive Coverage',
            'Corporate Governance and CSR',
            'SEBI Regulations Basics',
            'Income Tax Act - Basic Provisions',
            'Tax Planning and Management',
            'GST - Comprehensive Coverage',
            'TDS and TCS Provisions',
            'E-filing and Tax Compliance',
            'Transfer Pricing Basics'
          ],
          practicals: ['Company Registration Process', 'GST Return Filing', 'Income Tax Return Preparation', 'Tax Software Training']
        },
        {
          name: 'Business Analytics & ERP Systems',
          duration: '8 weeks',
          topics: [
            'Advanced Excel - Formulas and Functions',
            'Excel VBA and Macros',
            'Power BI Basics',
            'Data Visualization Techniques',
            'Tally ERP 9 - Advanced Features',
            'SAP FICO Overview',
            'Business Process Automation',
            'MIS Reporting'
          ],
          practicals: ['Live Project on Financial Dashboard', 'ERP Implementation Case Study', 'Data Analysis Competition']
        },
        {
          name: 'Professional Skills & Ethics',
          duration: '6 weeks',
          topics: [
            'Professional Ethics and Code of Conduct',
            'Communication Skills - Written and Verbal',
            'Presentation Skills',
            'Time Management and Productivity',
            'Team Building and Leadership',
            'Interview Skills and Resume Writing',
            'Linkedin Profile Optimization',
            'Personal Branding for Accountants'
          ],
          practicals: ['Mock Interviews', 'Group Discussions', 'Case Presentation Competitions', 'Networking Sessions']
        },
        {
          name: 'ACCA/CPA Primer (Bonus Module)',
          duration: '6 weeks',
          topics: [
            'Introduction to ACCA Qualification',
            'IFRS - International Financial Reporting Standards',
            'US GAAP Basics',
            'International Taxation Overview',
            'CPA Exam Structure and Preparation',
            'Global Accounting Careers',
            'UK and US Job Market for Accountants'
          ],
          practicals: ['IFRS vs GAAP Comparison Projects', 'International Case Studies', 'Global Firm Interactions']
        }
      ],
      careerPath: [
        'CA Intermediate with Global Perspective',
        'Dual Qualification (CA + ACCA/CPA)',
        'International Articleship Opportunities',
        'Big 4 Placement',
        'Global Finance Roles'
      ],
      internshipDetails: {
        duration: '10 weeks (4 weeks + 6 weeks)',
        partners: ['Deloitte', 'EY', 'PwC', 'KPMG', 'Grant Thornton', 'BDO', 'Top CA Firms'],
        activities: [
          'Audit and Assurance Projects',
          'Tax Compliance and Planning',
          'Corporate Finance Analysis',
          'Internal Audit',
          'Forensic Accounting',
          'International Projects Exposure'
        ]
      }
    }
  ];

  const internationalPrograms = [
    {
      id: 'int-acca-6m',
      title: 'Global Accounting - 6 Month Bridge (ACCA/CPA Primer)',
      tagline: 'IFRS, International Tax & Global certification readiness',
      duration: '6 months',
      startDate: 'Dec 1, 2025',
      fees: '₹34,999',
      seats: 25,
      delivery: 'Online Live + Virtual Internships',
      eligibility: 'UG, PG Students',
      certification: 'ACCA Knowledge Level Certificate',
      highlights: [
        'IFRS vs Indian AS comprehensive comparison',
        'International taxation - UAE VAT, UK tax overview',
        'ACCA knowledge modules (F1, F2, F3)',
        'CPA exam orientation',
        'Virtual internships with international mentors',
        'Global accounting software training (QuickBooks, Xero)',
        'International job market guidance'
      ],
      weeklySchedule: {
        'Monday': ['9:00 AM - IFRS Fundamentals', '2:00 PM - Case Studies', '5:00 PM - Discussion Forum'],
        'Tuesday': ['9:00 AM - International Tax', '2:00 PM - UAE VAT Workshop', '5:00 PM - Expert Session'],
        'Wednesday': ['9:00 AM - ACCA F1 Module', '2:00 PM - Business Analysis', '5:00 PM - Group Project'],
        'Thursday': ['9:00 AM - ACCA F2 Module', '2:00 PM - Management Accounting', '5:00 PM - Practice Tests'],
        'Friday': ['9:00 AM - ACCA F3 Module', '2:00 PM - Financial Accounting', '5:00 PM - Mock Exam'],
        'Saturday': ['Virtual Internship Activities', 'Software Training', 'Mentor Sessions'],
        'Sunday': ['Self Study / Global Webinars / Networking Events']
      },
      modules: [
        {
          name: 'IFRS Essentials',
          duration: '8 weeks',
          topics: [
            'Introduction to IFRS Framework',
            'IAS 1 - Presentation of Financial Statements',
            'IAS 2 - Inventories',
            'IAS 16 - Property, Plant and Equipment',
            'IAS 37 - Provisions, Contingent Liabilities',
            'IAS 38 - Intangible Assets',
            'IFRS 15 - Revenue from Contracts',
            'IFRS 16 - Leases',
            'IFRS 9 - Financial Instruments',
            'Comparison: IFRS vs Indian Accounting Standards'
          ],
          practicals: ['IFRS Financial Statement Preparation', 'Conversion from GAAP to IFRS', 'Real Company IFRS Analysis']
        },
        {
          name: 'International Taxation Basics',
          duration: '6 weeks',
          topics: [
            'Overview of Global Tax Systems',
            'UAE VAT - Comprehensive Guide',
            'UK Corporation Tax Basics',
            'US Federal Taxation Overview',
            'Transfer Pricing Fundamentals',
            'Double Taxation Avoidance Agreements (DTAA)',
            'Tax Planning for International Business',
            'E-commerce Taxation',
            'Digital Services Tax'
          ],
          practicals: ['UAE VAT Return Filing', 'International Tax Case Studies', 'Transfer Pricing Documentation']
        },
        {
          name: 'ACCA Knowledge Modules (F1, F2, F3)',
          duration: '10 weeks',
          topics: [
            'F1 - Accountant in Business: Business Organization, HR, Leadership',
            'F1 - Professional Ethics and Corporate Governance',
            'F2 - Management Accounting: Cost Classification and Behavior',
            'F2 - Budgeting and Standard Costing',
            'F2 - Performance Measurement',
            'F3 - Financial Accounting: Double Entry, Trial Balance',
            'F3 - Preparing Financial Statements',
            'F3 - Preparing Consolidated Financial Statements',
            'F3 - Interpreting Financial Statements'
          ],
          practicals: ['ACCA Practice MCQs', 'Mock Exams', 'Online Assessment Preparation', 'CBE Training']
        },
        {
          name: 'CPA Orientation & US GAAP',
          duration: '4 weeks',
          topics: [
            'CPA Exam Structure and Requirements',
            'US CPA Career Pathways',
            'Introduction to US GAAP',
            'Key Differences: IFRS vs US GAAP',
            'FAR - Financial Accounting & Reporting Overview',
            'AUD - Auditing & Attestation Overview',
            'REG - Regulation Overview',
            'BEC - Business Environment & Concepts Overview'
          ],
          practicals: ['CPA Sample Questions', 'US GAAP Case Studies', 'Career Planning Session']
        },
        {
          name: 'Global Accounting Software & Tools',
          duration: '4 weeks',
          topics: [
            'QuickBooks Online - Complete Training',
            'Xero Accounting Software',
            'Sage Accounting',
            'Microsoft Dynamics 365',
            'Oracle Financials Basics',
            'Cloud Accounting Best Practices',
            'International Payroll Systems',
            'Multi-currency Accounting'
          ],
          practicals: ['QuickBooks Certification Project', 'Xero Hands-on Labs', 'Software Comparison Analysis']
        }
      ],
      careerPath: [
        'ACCA Strategic Professional Level',
        'US CPA Certification',
        'International Accounting Roles',
        'Big 4 International Offices',
        'UAE/UK/US Job Market'
      ],
      internshipDetails: {
        duration: '4 weeks virtual',
        partners: ['International CPA Firms', 'UAE-based Accounting Firms', 'UK Chartered Accountants', 'US Remote Accounting Firms'],
        activities: ['Virtual Audit Projects', 'IFRS Financial Reporting', 'International Tax Research', 'Global Client Interactions']
      }
    },
    {
      id: 'int-global-1y',
      title: 'Global Accounting - 1 Year Professional Pathway',
      tagline: 'ACCA/CPA comprehensive preparation + International placements',
      duration: '12 months',
      startDate: 'Feb 5, 2026',
      fees: '₹79,999',
      seats: 20,
      delivery: 'Hybrid Global Mentorship + In-person Labs',
      eligibility: 'UG, PG, Working Professionals',
      certification: 'ACCA Advanced + CPA Ready Certificate',
      highlights: [
        'Complete ACCA foundation and skills level',
        'US CPA comprehensive preparation',
        'IFRS, ISA, International tax expert training',
        'UAE/UK/US market readiness program',
        'Guaranteed international internship',
        'Placement support for global roles',
        'Capstone global finance project',
        'Resume and LinkedIn optimization',
        'Interview coaching by international recruiters'
      ],
      weeklySchedule: {
        'Monday-Thursday': ['Intensive ACCA/CPA modules with live global instructors'],
        'Friday': ['Mock exams, performance review, mentor feedback'],
        'Saturday': ['Practical labs, software training, case competitions'],
        'Sunday': ['Internship projects, networking events, career counseling']
      },
      modules: [
        {
          name: 'ACCA Knowledge & Skills Modules (Complete)',
          duration: '20 weeks',
          topics: [
            'F1 - Accountant in Business',
            'F2 - Management Accounting',
            'F3 - Financial Accounting',
            'F4 - Corporate & Business Law',
            'F5 - Performance Management',
            'F6 - Taxation',
            'F7 - Financial Reporting',
            'F8 - Audit and Assurance',
            'F9 - Financial Management'
          ],
          practicals: ['All ACCA CBE Simulations', 'Practice Mock Exams', 'Real Exam Environment Training']
        },
        {
          name: 'IFRS & International Financial Reporting',
          duration: '10 weeks',
          topics: [
            'Advanced IFRS - All Standards in Depth',
            'IFRS for SMEs',
            'First-time Adoption of IFRS',
            'Group Accounting and Consolidations',
            'Fair Value Measurement',
            'Revenue Recognition (IFRS 15)',
            'Lease Accounting (IFRS 16)',
            'Financial Instruments (IFRS 9)',
            'Insurance Contracts (IFRS 17)',
            'Sustainability Reporting Standards'
          ],
          practicals: ['Complex Consolidation Projects', 'IFRS Financial Statement Preparation', 'Industry-specific IFRS Application']
        },
        {
          name: 'International Tax & Transfer Pricing',
          duration: '8 weeks',
          topics: [
            'International Tax Planning Strategies',
            'Tax Treaties and DTAA',
            'Transfer Pricing - OECD Guidelines',
            'BEPS (Base Erosion and Profit Shifting)',
            'Country-by-Country Reporting',
            'Permanent Establishment Concepts',
            'Withholding Tax on International Transactions',
            'Tax Havens and Anti-avoidance Rules',
            'Digital Economy Taxation',
            'UAE Corporate Tax (2023 onwards)'
          ],
          practicals: ['Transfer Pricing Documentation Project', 'International Tax Structuring Case Studies', 'BEPS Implementation Analysis']
        },
        {
          name: 'US CPA Comprehensive Preparation',
          duration: '16 weeks',
          topics: [
            'FAR - Financial Accounting & Reporting (Complete)',
            'US GAAP Standards in Depth',
            'Government and Non-profit Accounting',
            'AUD - Auditing & Attestation (Complete)',
            'US Auditing Standards',
            'PCAOB and SEC Regulations',
            'REG - Regulation (Complete)',
            'Federal Taxation - Individual and Business',
            'Business Law and Ethics',
            'BEC - Business Environment & Concepts (Complete)',
            'Corporate Governance and IT Controls'
          ],
          practicals: ['CPA Exam Simulations', 'Becker/Wiley CPA Review Integration', 'Mock Exams for all 4 Sections']
        },
        {
          name: 'Global Financial Modeling & Analytics',
          duration: '8 weeks',
          topics: [
            'Advanced Excel for Finance Professionals',
            'Financial Modeling Best Practices',
            'Valuation Techniques - DCF, Comparables',
            'LBO and M&A Modeling',
            'Scenario and Sensitivity Analysis',
            'Power BI for Financial Reporting',
            'Python for Finance (Basics)',
            'SQL for Accountants',
            'Data Analytics in Auditing',
            'Blockchain and Cryptocurrency Accounting'
          ],
          practicals: ['Build Complete Financial Models', 'Real Company Valuation Project', 'Data Analytics Competition']
        },
        {
          name: 'International Audit & Assurance',
          duration: '6 weeks',
          topics: [
            'International Standards on Auditing (ISA)',
            'Risk-based Audit Approach',
            'Internal Audit Best Practices',
            'SOX Compliance and Internal Controls',
            'Forensic Accounting and Fraud Detection',
            'IT Audit Fundamentals',
            'ESG and Sustainability Auditing',
            'Quality Control Standards (ISQC)'
          ],
          practicals: ['Audit Planning and Execution Project', 'Internal Control Testing', 'Forensic Case Studies']
        },
        {
          name: 'Career Readiness & Professional Development',
          duration: '4 weeks',
          topics: [
            'International Resume Writing',
            'LinkedIn Profile Optimization for Global Market',
            'Interview Skills for Big 4 and MNCs',
            'Networking Strategies',
            'Personal Branding',
            'Cross-cultural Communication',
            'Negotiation Skills',
            'Work Visa and Immigration Guidance (UAE/UK/US/Canada)'
          ],
          practicals: ['Mock Interviews with International Recruiters', 'Resume Clinic', 'Networking Events with Global Professionals']
        }
      ],
      careerPath: [
        'ACCA Qualified Accountant',
        'US CPA License',
        'Big 4 International Offices (UAE, UK, US)',
        'Financial Controller in MNCs',
        'International Tax Consultant',
        'CFO Track in Global Companies'
      ],
      internshipDetails: {
        duration: '12 weeks (can be extended)',
        partners: [
          'Deloitte Middle East',
          'EY UK',
          'PwC US',
          'KPMG Global',
          'Grant Thornton International',
          'BDO Global',
          'International Corporations in UAE',
          'US Remote CPA Firms',
          'UK Chartered Accountancy Firms'
        ],
        activities: [
          'International Audit Engagements',
          'IFRS Financial Statement Preparation',
          'Transfer Pricing Documentation',
          'Tax Compliance for Multinationals',
          'M&A Due Diligence',
          'Internal Audit Projects',
          'ESG Reporting Initiatives'
        ]
      }
    }
  ];

  const allPrograms = mode === 'national' ? nationalPrograms : internationalPrograms;

  const validateForm = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = 'Valid email required';
    if (!/^\d{7,15}$/.test(form.phone)) errs.phone = 'Valid phone (7-15 digits)';
    if (!form.programId) errs.programId = 'Please select a program';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submitEnrollment = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 1000));
    setSuccessMsg('Enrollment submitted successfully! We will contact you within 24 hours.');
    setForm({ name: '', email: '', phone: '', programId: '', course: '' });
    setSubmitting(false);
    setTimeout(() => {
      setShowEnrollModal(false);
      setSuccessMsg('');
    }, 3000);
  };

  const openProgramModal = (program) => {
    setSelectedProgram(program);
    setShowModal(true);
  };

  const openEnrollModal = (program) => {
    setSelectedProgram(program);
    setForm({ ...form, programId: program.id, course: program.title });
    setShowEnrollModal(true);
  };

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', minHeight: '100vh', background: '#f5f7fa' }}>
  

      {/* Hero */}
      <div style={{
        paddingTop: '100px',
        paddingBottom: '40px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div className="container text-center">
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 'bold', marginBottom: '15px' }}>
            CA & Global Finance Programs
          </h1>
          <p style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', maxWidth: '700px', margin: '0 auto 25px' }}>
            IIT-style conceptual training + Industry internships + National (ICAI) & International (ACCA/CPA) pathways
          </p>

          <div className="btn-group mt-3" role="group">
            <button
              className={`btn ${mode === 'national' ? 'btn-light' : 'btn-outline-light'}`}
              style={{ fontWeight: 'bold', padding: '12px 30px' }}
              onClick={() => setMode(<CATrainingIndia/>)}
            >
              <FaFlag className="me-2" /> National (ICAI)
            </button>
            <button
              className={`btn ${mode === 'international' ? 'btn-light' : 'btn-outline-light'}`}
              style={{ fontWeight: 'bold', padding: '12px 30px' }}
              onClick={() => setMode(<CharteredAccountantPlatform/>)}
            >
              <FaGlobe className="me-2" /> International (ACCA/CPA)
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-5">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="row g-4">
            <div className="col-12">
              <h2 style={{ color: '#667eea', marginBottom: '20px' }}>
                <FaStar className="me-2" /> Why Choose Olewin Academy?
              </h2>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center">
                  <FaBookOpen style={{ fontSize: '48px', color: '#667eea', marginBottom: '15px' }} />
                  <h5>IIT-Style Training</h5>
                  <p className="text-muted small">Conceptual depth with problem-solving excellence</p>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center">
                  <FaBriefcase style={{ fontSize: '48px', color: '#667eea', marginBottom: '15px' }} />
                  <h5>Real Internships</h5>
                  <p className="text-muted small">2-12 weeks with top CA firms & corporates</p>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center">
                  <FaGlobe style={{ fontSize: '48px', color: '#667eea', marginBottom: '15px' }} />
                  <h5>Dual Pathways</h5>
                  <p className="text-muted small">National (CA) + International (ACCA/CPA)</p>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center">
                  <FaChartLine style={{ fontSize: '48px', color: '#667eea', marginBottom: '15px' }} />
                  <h5>Performance Tracking</h5>
                  <p className="text-muted small">Data-driven dashboards & mentor feedback</p>
                </div>
              </div>
            </div>

            <div className="col-12 mt-5">
              <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e6e9ff 100%)' }}>
                <div className="card-body p-4">
                  <h4 className="mb-3" style={{ color: '#667eea' }}>
                    <FaInfoCircle className="me-2" /> What Makes Us Different?
                  </h4>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <ul className="list-unstyled">
                        <li className="mb-2"><FaCheckCircle style={{ color: '#667eea' }} className="me-2" /> Structured syllabus mapped to ICAI + ACCA/CPA</li>
                        <li className="mb-2"><FaCheckCircle style={{ color: '#667eea' }} className="me-2" /> Real internships with evaluation reports</li>
                        <li className="mb-2"><FaCheckCircle style={{ color: '#667eea' }} className="me-2" /> Weekly quizzes & monthly mock tests</li>
                        <li className="mb-2"><FaCheckCircle style={{ color: '#667eea' }} className="me-2" /> Flexible batches (weekday/weekend)</li>
                      </ul>
                    </div>
                    <div className="col-12 col-md-6">
                      <ul className="list-unstyled">
                        <li className="mb-2"><FaCheckCircle style={{ color: '#667eea' }} className="me-2" /> Affordable fees with EMI options</li>
                        <li className="mb-2"><FaCheckCircle style={{ color: '#667eea' }} className="me-2" /> Practical software training (Tally, SAP, QuickBooks)</li>
                        <li className="mb-2"><FaCheckCircle style={{ color: '#667eea' }} className="me-2" /> Placement assistance & resume coaching</li>
                        <li className="mb-2"><FaCheckCircle style={{ color: '#667eea' }} className="me-2" /> Industry expert guest lectures</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Programs Tab */}
        {activeTab === 'programs' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 style={{ color: '#667eea' }}>
                <FaBook className="me-2" /> Available Programs ({mode === 'national' ? 'National' : 'International'})
              </h2>
            </div>

            <div className="row g-4">
              {allPrograms.map(program => (
                <div key={program.id} className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-12 col-lg-8">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h4 style={{ color: '#333', marginBottom: '8px' }}>{program.title}</h4>
                            <span className="badge bg-primary" style={{ fontSize: '14px', padding: '8px 15px' }}>
                              {program.duration}
                            </span>
                          </div>
                          <p className="text-muted mb-3">{program.tagline}</p>

                          <div className="row g-3 small">
                            <div className="col-6 col-sm-3">
                              <FaCalendarAlt className="text-primary me-2" />
                              <strong>Start:</strong><br />
                              <span className="text-muted">{program.startDate}</span>
                            </div>
                            <div className="col-6 col-sm-3">
                              <FaMoneyBillWave className="text-primary me-2" />
                              <strong>Fees:</strong><br />
                              <span className="text-muted">{program.fees}</span>
                            </div>
                            <div className="col-6 col-sm-3">
                              <FaLaptop className="text-primary me-2" />
                              <strong>Delivery:</strong><br />
                              <span className="text-muted">{program.delivery}</span>
                            </div>
                            <div className="col-6 col-sm-3">
                              <FaUsers className="text-primary me-2" />
                              <strong>Seats:</strong><br />
                              <span className="text-muted">{program.seats} left</span>
                            </div>
                          </div>

                          <div className="mt-3">
                            <div className="d-flex flex-wrap gap-2">
                              {program.highlights.slice(0, 3).map((highlight, idx) => (
                                <span key={idx} className="badge bg-light text-dark" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                                  <FaCheckCircle style={{ color: '#667eea', fontSize: '10px' }} className="me-1" />
                                  {highlight}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="col-12 col-lg-4 d-flex flex-column justify-content-center align-items-end gap-2">
                          <button
                            className="btn btn-outline-primary w-100"
                            onClick={() => openProgramModal(program)}
                          >
                            <FaInfoCircle className="me-2" /> View Full Details
                          </button>
                          <button
                            className="btn btn-primary w-100"
                            onClick={() => openEnrollModal(program)}
                          >
                            <FaRocket className="me-2" /> Enroll Now
                          </button>
                          <button className="btn btn-outline-secondary w-100">
                            <FaDownload className="me-2" /> Download Brochure
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Syllabus Tab */}
        {activeTab === 'syllabus' && (
          <div>
            <h2 style={{ color: '#667eea', marginBottom: '20px' }}>
              <FaRegFileAlt className="me-2" /> Complete Syllabus & Module Details
            </h2>

            {allPrograms.map(program => (
              <div key={program.id} className="mb-5">
                <div className="card border-0 shadow-sm">
                  <div className="card-header" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                    <h4 className="mb-0">{program.title}</h4>
                    <small>{program.tagline}</small>
                  </div>
                  <div className="card-body">
                    <div className="mb-4">
                      <h5 style={{ color: '#667eea' }}>
                        <FaClock className="me-2" /> Weekly Schedule
                      </h5>
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead style={{ background: '#f8f9fa' }}>
                            <tr>
                              <th>Day</th>
                              <th>Schedule</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(program.weeklySchedule).map(([day, schedule]) => (
                              <tr key={day}>
                                <td><strong>{day}</strong></td>
                                <td>
                                  {Array.isArray(schedule) ? (
                                    <ul className="mb-0" style={{ paddingLeft: '20px' }}>
                                      {schedule.map((item, idx) => (
                                        <li key={idx}>{item}</li>
                                      ))}
                                    </ul>
                                  ) : schedule}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <h5 style={{ color: '#667eea', marginTop: '30px' }}>
                      <FaBook className="me-2" /> Course Modules
                    </h5>

                    {program.modules.map((module, idx) => (
                      <div key={idx} className="mb-3">
                        <div
                          className="card"
                          style={{
                            cursor: 'pointer',
                            border: expandedModule === `${program.id}-${idx}` ? '2px solid #667eea' : '1px solid #dee2e6'
                          }}
                          onClick={() => setExpandedModule(expandedModule === `${program.id}-${idx}` ? null : `${program.id}-${idx}`)}
                        >
                          <div className="card-header bg-white d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-0" style={{ color: '#333' }}>{module.name}</h6>
                              <small className="text-muted">Duration: {module.duration}</small>
                            </div>
                            {expandedModule === `${program.id}-${idx}` ? <FaChevronUp /> : <FaChevronDown />}
                          </div>

                          {expandedModule === `${program.id}-${idx}` && (
                            <div className="card-body">
                              <h6 style={{ color: '#667eea' }}>Topics Covered:</h6>
                              <div className="row">
                                <div className="col-12">
                                  <ul>
                                    {module.topics.map((topic, topicIdx) => (
                                      <li key={topicIdx} className="mb-2">{topic}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>

                              {module.practicals && (
                                <>
                                  <h6 style={{ color: '#667eea' }} className="mt-3">Practical Training:</h6>
                                  <div className="d-flex flex-wrap gap-2">
                                    {module.practicals.map((practical, pIdx) => (
                                      <span key={pIdx} className="badge bg-success" style={{ fontSize: '13px', padding: '8px 12px' }}>
                                        <FaPlay className="me-1" style={{ fontSize: '10px' }} />
                                        {practical}
                                      </span>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Internships Tab */}
        {activeTab === 'internships' && (
          <div>
            <h2 style={{ color: '#667eea', marginBottom: '20px' }}>
              <FaBriefcase className="me-2" /> Internship & Placement Program
            </h2>

            <div className="row g-4">
              {allPrograms.map(program => (
                <div key={program.id} className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header" style={{ background: '#f8f9fa', borderBottom: '2px solid #667eea' }}>
                      <h5 className="mb-0">{program.title}</h5>
                    </div>
                    <div className="card-body">
                      <div className="row g-4">
                        <div className="col-12 col-md-4">
                          <div className="text-center p-3" style={{ background: '#f0f4ff', borderRadius: '10px' }}>
                            <FaClock style={{ fontSize: '40px', color: '#667eea', marginBottom: '10px' }} />
                            <h6>Duration</h6>
                            <p className="mb-0">{program.internshipDetails.duration}</p>
                          </div>
                        </div>

                        <div className="col-12 col-md-8">
                          <h6 style={{ color: '#667eea' }}>
                            <FaBuilding className="me-2" /> Partner Organizations
                          </h6>
                          <div className="d-flex flex-wrap gap-2 mb-3">
                            {program.internshipDetails.partners.map((partner, idx) => (
                              <span key={idx} className="badge bg-primary" style={{ fontSize: '13px', padding: '8px 15px' }}>
                                {partner}
                              </span>
                            ))}
                          </div>

                          <h6 style={{ color: '#667eea' }}>
                            <FaCheckCircle className="me-2" /> Activities & Exposure
                          </h6>
                          <ul className="mb-0">
                            {program.internshipDetails.activities.map((activity, idx) => (
                              <li key={idx}>{activity}</li>
                            ))}
                          </ul>
                        </div>

                        {program.careerPath && (
                          <div className="col-12">
                            <div className="card" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e6e9ff 100%)', border: 'none' }}>
                              <div className="card-body">
                                <h6 style={{ color: '#667eea' }}>
                                  <FaRocket className="me-2" /> Career Progression Path
                                </h6>
                                <div className="d-flex flex-wrap gap-3 mt-3">
                                  {program.careerPath.map((step, idx) => (
                                    <div key={idx} className="d-flex align-items-center">
                                      <div className="badge bg-white text-primary" style={{ fontSize: '16px', fontWeight: 'bold', width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {idx + 1}
                                      </div>
                                      <span className="ms-2">{step}</span>
                                      {idx < program.careerPath.length - 1 && (
                                        <FaChevronDown style={{ margin: '0 15px', color: '#667eea', transform: 'rotate(-90deg)' }} />
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5">
              <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <div className="card-body p-4">
                  <h4 className="mb-3">Internship Benefits</h4>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <FaCheckCircle className="me-2" /> Real-world project experience
                    </div>
                    <div className="col-12 col-md-6">
                      <FaCheckCircle className="me-2" /> Mentor evaluation & feedback reports
                    </div>
                    <div className="col-12 col-md-6">
                      <FaCheckCircle className="me-2" /> Professional network building
                    </div>
                    <div className="col-12 col-md-6">
                      <FaCheckCircle className="me-2" /> Industry exposure certificate
                    </div>
                    <div className="col-12 col-md-6">
                      <FaCheckCircle className="me-2" /> Full-time placement opportunities
                    </div>
                    <div className="col-12 col-md-6">
                      <FaCheckCircle className="me-2" /> Resume building & LinkedIn recommendations
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === 'contact' && (
          <div>
            <h2 style={{ color: '#667eea', marginBottom: '20px' }}>
              <FaPhoneAlt className="me-2" /> Get In Touch
            </h2>

            <div className="row g-4">
              <div className="col-12 col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h5 className="mb-4">Contact Information</h5>
                    
                    <div className="mb-3">
                      <FaMapMarkerAlt style={{ color: '#667eea', fontSize: '20px' }} className="me-3" />
                      <strong>Address:</strong><br />
                      <span className="ms-5 text-muted">Olewin Academy, Global Campus<br />Mumbai, Maharashtra 400001, India</span>
                    </div>

                    <div className="mb-3">
                      <FaPhoneAlt style={{ color: '#667eea', fontSize: '20px' }} className="me-3" />
                      <strong>Phone:</strong><br />
                      <span className="ms-5 text-muted">+91 98765 43210<br />+91 87654 32109</span>
                    </div>

                    <div className="mb-3">
                      <FaEnvelope style={{ color: '#667eea', fontSize: '20px' }} className="me-3" />
                      <strong>Email:</strong><br />
                      <span className="ms-5 text-muted">admissions@olewinacademy.com<br />info@olewinacademy.com</span>
                    </div>

                    <div className="mb-3">
                      <FaClock style={{ color: '#667eea', fontSize: '20px' }} className="me-3" />
                      <strong>Office Hours:</strong><br />
                      <span className="ms-5 text-muted">Mon-Fri: 9:00 AM - 7:00 PM<br />Sat: 10:00 AM - 5:00 PM<br />Sun: Closed</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h5 className="mb-4">Quick Inquiry Form</h5>
                    <form>
                      <div className="mb-3">
                        <label className="form-label">Full Name</label>
                        <input type="text" className="form-control" placeholder="Enter your name" />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Email</label>
                        <input type="email" className="form-control" placeholder="Enter your email" />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Phone</label>
                        <input type="tel" className="form-control" placeholder="Enter your phone" />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Interested In</label>
                        <select className="form-select">
                          <option value="">Select program</option>
                          <option>National CA Programs</option>
                          <option>International ACCA/CPA Programs</option>
                          <option>Both</option>
                        </select>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Message</label>
                        <textarea className="form-control" rows="3" placeholder="Your message..."></textarea>
                      </div>
                      <button type="submit" className="btn btn-primary w-100">
                        <FaEnvelope className="me-2" /> Send Inquiry
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Program Detail Modal */}
      {showModal && selectedProgram && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setShowModal(false)}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <h5 className="modal-title">{selectedProgram.title}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <p className="lead">{selectedProgram.tagline}</p>

                <div className="row g-3 mb-4">
                  <div className="col-6">
                    <strong><FaClock className="text-primary me-2" />Duration:</strong><br />
                    {selectedProgram.duration}
                  </div>
                  <div className="col-6">
                    <strong><FaMoneyBillWave className="text-primary me-2" />Fees:</strong><br />
                    {selectedProgram.fees}
                  </div>
                  <div className="col-6">
                    <strong><FaCalendarAlt className="text-primary me-2" />Start Date:</strong><br />
                    {selectedProgram.startDate}
                  </div>
                  <div className="col-6">
                    <strong><FaCertificate className="text-primary me-2" />Certification:</strong><br />
                    {selectedProgram.certification}
                  </div>
                </div>

                <h6 style={{ color: '#667eea' }}>Program Highlights:</h6>
                <ul>
                  {selectedProgram.highlights.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>

                <h6 style={{ color: '#667eea' }} className="mt-3">Course Modules:</h6>
                {selectedProgram.modules.map((m, i) => (
                  <div key={i} className="mb-2">
                    <strong>{i + 1}. {m.name}</strong> <span className="text-muted">({m.duration})</span>
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={() => {
                  setShowModal(false);
                  openEnrollModal(selectedProgram);
                }}>
                  <FaRocket className="me-2" /> Enroll Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enrollment Modal */}
      {showEnrollModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setShowEnrollModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enroll - {selectedProgram?.title}</h5>
                <button type="button" className="btn-close" onClick={() => setShowEnrollModal(false)}></button>
              </div>
              <form onSubmit={submitEnrollment}>
                <div className="modal-body">
                  {successMsg && (
                    <div className="alert alert-success">
                      <FaCheckCircle className="me-2" />{successMsg}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                    />
                    {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                    />
                    {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Phone *</label>
                    <input
                      type="tel"
                      className={`form-control ${formErrors.phone ? 'is-invalid' : ''}`}
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                    />
                    {formErrors.phone && <div className="invalid-feedback">{formErrors.phone}</div>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Selected Program</label>
                    <input type="text" className="form-control" value={form.course} readOnly />
                  </div>

                  <div className="form-text">
                    By enrolling, you agree to our terms and conditions. We'll contact you within 24 hours with next steps.
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowEnrollModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Enrollment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

  

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .card {
          transition: all 0.3s ease;
        }
        .card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.15) !important;
        }
        .btn {
          transition: all 0.3s ease;
        }
        .btn:hover {
          transform: translateY(-2px);
        }
        .badge {
          transition: all 0.3s ease;
        }
        .badge:hover {
          transform: scale(1.05);
        }
        * {
          box-sizing: border-box;
        }
        .modal {
          animation: fadeIn 0.3s ease;
        }
        .modal-dialog {
          animation: slideIn 0.3s ease;
        }
        @media (max-width: 768px) {
          .modal-lg {
            max-width: 95%;
          }
        }
      `}</style>
    </div>
  );
};

export default CATraining;